/**
 * Catalog setup — the three lists the catalog itself is described in.
 *
 * `docs/catalog-and-packages.md` §6.2 moves these off the Catalog page, and
 * the reason is worth stating: a category, a unit and a measurement type are
 * not things a quote is built out of. They are the vocabulary the things a
 * quote *is* built out of are written in. Mixed into the same tab strip, they
 * made the catalog nine tabs deep and buried the four anybody opens daily.
 *
 * Set up rarely, changed rarely, and dangerous to change carelessly — renaming
 * a unit reaches every item that uses it — which is a second reason to keep
 * them out of the way of the tab somebody clicks twenty times a day.
 */
import { useState } from 'react'
import type { MeResponse } from '@rowted/shared'
import { CatalogList } from '../components/catalog-list.js'
import { RecordTabs } from '../record/record-tabs.js'
import { Tag } from '../components/primitives.js'
import type { ListRow } from '../lib/use-list.js'
import { useSettingsBar } from '../settings/settings-bar.js'

/** What a unit measures, in the order a picker groups them. */
const UNIT_KINDS = [
  { value: 'count', label: 'Count' },
  { value: 'pack', label: 'Packaging' },
  { value: 'area', label: 'Area' },
  { value: 'length', label: 'Length' },
  { value: 'volume', label: 'Volume' },
  { value: 'weight', label: 'Weight' },
  { value: 'time', label: 'Time' },
]

const nameAnd = (row: ListRow) => ({
  primary: String(row.name ?? ''),
  secondary: String(row.description ?? '') || undefined,
})

const TABS = [
  { key: 'categories', label: 'Categories', icon: 'category' as const },
  { key: 'units', label: 'Units', icon: 'unit' as const },
  { key: 'measurements', label: 'Measurement types', icon: 'number' as const },
]

export function CatalogSetup({ me }: { me: MeResponse }) {
  const [tab, setTab] = useState('categories')
  const [reloadKey, setReloadKey] = useState(0)
  const changed = () => setReloadKey((key) => key + 1)

  const canEdit = me.permissions.company_settings === 'edit'
    || me.permissions.company_settings === 'full'

  // The shell owns the page bar (`design-remediation.md` §1.1).
  useSettingsBar({
    title: 'Catalog setup',
    sub: 'the words the catalog is described in — categories, units and measurement types',
  })

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <RecordTabs tabs={TABS} active={tab} onChange={setTab} />

      <div className="p-4">
        {tab === 'categories' && (
          <CatalogList
            area="catalog-admin/item-categories"
            title="Categories"
            kind="item"
            singular="category"
            canEdit={canEdit}
            importOrgUnitId={me.scope.selected[0]}
            reloadKey={reloadKey}
            onChanged={changed}
            primary={nameAnd}
            blank={{ name: '', description: '', qboAccount: null, sortOrder: 0, active: true }}
            fields={[
              { name: 'name', label: 'Name', required: true, placeholder: 'Mulch and soil' },
              { name: 'description', label: 'Description', kind: 'textarea' },
              { name: 'qboAccount', label: 'Accounting code',
                hint: 'Where a verified receipt’s line posts in the books.' },
              { name: 'sortOrder', label: 'Sort order', kind: 'number' },
              { name: 'active', label: 'Active', kind: 'switch' },
            ]}
            emptyTitle="No categories yet"
            emptyBody="A category is what a material is filed under, and what a verified receipt’s line posts to. Four or five covers most companies."
          />
        )}

        {tab === 'units' && (
          <CatalogList
            area="catalog-admin/units"
            title="Units"
            kind="item"
            singular="unit"
            canEdit={canEdit}
            importOrgUnitId={me.scope.selected[0]}
            reloadKey={reloadKey}
            onChanged={changed}
            primary={nameAnd}
            cell={{
              code: (row) => <code className="text-cell">{String(row.code ?? '')}</code>,
              kind: (row) => (
                <Tag>
                  {UNIT_KINDS.find((entry) => entry.value === row.kind)?.label
                    ?? String(row.kind ?? '')}
                </Tag>
              ),
            }}
            blank={{ code: '', name: '', kind: 'count', description: '', sortOrder: 0,
                     active: true }}
            fields={[
              { name: 'name', label: 'Name', required: true, placeholder: 'Square foot' },
              { name: 'code', label: 'Short form', required: true, placeholder: 'sf',
                hint: 'What gets printed next to a number, on a quote and on a visit.' },
              { name: 'kind', label: 'Measures', kind: 'select', required: true,
                options: UNIT_KINDS },
              { name: 'description', label: 'Description', kind: 'textarea' },
              { name: 'sortOrder', label: 'Sort order', kind: 'number' },
              { name: 'active', label: 'Active', kind: 'switch' },
            ]}
            emptyTitle="No units yet"
            emptyBody="A unit is how much of something there is. Every company starts with the standard set; add the ones your suppliers use."
          />
        )}

        {tab === 'measurements' && (
          <>
            <CatalogList
              area="catalog-admin/measurement-types"
              title="Measurement types"
              kind="item"
              singular="measurement type"
              canEdit={canEdit}
              importOrgUnitId={me.scope.selected[0]}
              reloadKey={reloadKey}
              onChanged={changed}
              primary={nameAnd}
              blank={{ name: '', unit: '', description: '', sortOrder: 0, active: true }}
              fields={[
                { name: 'name', label: 'Name', required: true, placeholder: 'Turf area' },
                { name: 'unit', label: 'Measured in', required: true, placeholder: 'sq ft' },
                { name: 'description', label: 'Description', kind: 'textarea' },
                { name: 'sortOrder', label: 'Sort order', kind: 'number' },
                { name: 'active', label: 'Active', kind: 'switch' },
              ]}
              emptyTitle="Nothing gets measured yet"
              emptyBody="A measurement type is what a property gets measured for — turf area, bed area, driveway. A package priced by the square foot reads one of these off the site."
            />
            {/* The values live on the property, which is where somebody
                walking a site puts them. */}
            <p className="mt-3 text-sub text-text-faint">
              A property is measured on its own profile — this is where you say what a
              measurement <em>means</em>, not what this lawn’s is.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
